/**
 * A formatação do WhatsApp no texto de uma mensagem (#615).
 *
 * `*negrito*`, `_itálico_`, `~riscado~`, `` `código` ``, ```` ```bloco``` ````,
 * listas com `* `, `- ` e `1. `, e citação com `> `. Sem isto, os marcadores
 * aparecem crus na bolha — inclusive na mensagem que o próprio produto monta,
 * a de Compartilhar contato (#595).
 *
 * ── Árvore, e não HTML ────────────────────────────────────────────────────
 *
 * O texto vem do cliente. Uma biblioteca de Markdown devolve HTML, e HTML de
 * cliente na nossa tela é o `dangerouslySetInnerHTML` que o `texto-com-links`
 * já recusou. Aqui o texto vira uma árvore de pedaços — cada um é texto, link,
 * menção ou um estilo com filhos —, e quem desenha é o React, que escapa o que
 * desenha. Um `<script>` escrito pelo cliente continua sendo as letras que é.
 *
 * ── Quando um marcador vale ───────────────────────────────────────────────
 *
 * As regras do WhatsApp, e o que elas evitam:
 *
 * - abre no começo ou depois de espaço/pontuação, e fecha antes de espaço,
 *   pontuação ou do fim: `2*3*4` e `arquivo_final_v2.pdf` ficam como estão;
 * - nada de espaço logo depois da abertura nem logo antes do fechamento:
 *   `* espaço *` fica como está;
 * - não atravessa linha, com exceção do bloco de código.
 *
 * Marcador sem par fica como texto (`*sem fechamento`, `~5 min`).
 *
 * ── O que é opaco ─────────────────────────────────────────────────────────
 *
 * Código, menção e endereço são achados ANTES dos estilos, e um estilo não os
 * corta ao meio: o `_` dentro de uma URL é da URL. Dentro de código nada é
 * interpretado — nem estilo, nem link.
 */

import { partir } from './texto-com-links';

export type Trecho =
  | { readonly tipo: 'texto'; readonly texto: string }
  | { readonly tipo: 'link'; readonly url: string; readonly href: string }
  | { readonly tipo: 'mencao'; readonly texto: string }
  | { readonly tipo: 'codigo'; readonly texto: string }
  | { readonly tipo: Estilo; readonly filhos: readonly Trecho[] };

export type Estilo = 'negrito' | 'italico' | 'riscado';

export interface ItemDeLista {
  /** O número que a pessoa escreveu, na lista numerada. `null` na de marcador. */
  readonly numero: number | null;
  readonly trechos: readonly Trecho[];
}

export type Bloco =
  /** Texto corrido, com as quebras de linha dentro (a bolha usa `pre-wrap`). */
  | { readonly tipo: 'paragrafo'; readonly trechos: readonly Trecho[] }
  | { readonly tipo: 'lista'; readonly ordenada: boolean; readonly itens: readonly ItemDeLista[] }
  | { readonly tipo: 'citacao'; readonly trechos: readonly Trecho[] }
  | { readonly tipo: 'bloco-de-codigo'; readonly texto: string };

const ESTILO_DO_MARCADOR: Readonly<Record<string, Estilo>> = {
  '*': 'negrito',
  _: 'italico',
  '~': 'riscado',
};

const LETRA_OU_DIGITO = /[\p{L}\p{N}]/u;
const ESPACO = /\s/;

/** O marcador em `i` pode abrir: começo, ou depois de espaço/pontuação. */
function podeAbrir(texto: string, i: number): boolean {
  return i === 0 || !LETRA_OU_DIGITO.test(texto[i - 1]!);
}

/** O marcador que termina antes de `fim` pode fechar: fim, ou espaço/pontuação depois. */
function podeFechar(texto: string, fim: number): boolean {
  return fim >= texto.length || !LETRA_OU_DIGITO.test(texto[fim]!);
}

// ── Os blocos ──────────────────────────────────────────────────────────────

const CERCA = '```';

/**
 * Os blocos de código, no texto inteiro: são a única marcação que atravessa
 * linha, então saem antes de o texto ser partido em linhas.
 */
function cercas(texto: string): { inicio: number; fim: number; conteudo: string }[] {
  const achadas: { inicio: number; fim: number; conteudo: string }[] = [];
  let i = texto.indexOf(CERCA);

  while (i !== -1) {
    if (!podeAbrir(texto, i)) {
      i = texto.indexOf(CERCA, i + 1);
      continue;
    }

    let j = texto.indexOf(CERCA, i + CERCA.length);
    while (
      j !== -1 &&
      (!podeFechar(texto, j + CERCA.length) || !/\S/.test(texto.slice(i + 3, j)))
    ) {
      j = texto.indexOf(CERCA, j + 1);
    }
    // Se esta abertura não achou fechamento, nenhuma depois dela acha: o
    // fechamento só depende do que vem depois. Parar aqui é o que impede uma
    // mensagem cheia de crases de custar tempo quadrático.
    if (j === -1) break;

    // A quebra logo depois da abertura e logo antes do fechamento é de quem
    // escreveu o bloco em linhas próprias, não do código.
    const conteudo = texto
      .slice(i + CERCA.length, j)
      .replace(/^\r?\n/, '')
      .replace(/\r?\n$/, '');
    achadas.push({ inicio: i, fim: j + CERCA.length, conteudo });
    i = texto.indexOf(CERCA, j + CERCA.length);
  }

  return achadas;
}

const LINHA_DE_LISTA = /^[*-] (.*\S.*)$/;
const LINHA_NUMERADA = /^(\d{1,9})\. (.*\S.*)$/;
const LINHA_DE_CITACAO = /^> (.*\S.*)$/;

/**
 * As linhas de um pedaço de texto sem bloco de código, agrupadas em blocos.
 *
 * Cada linha leva a própria quebra: no parágrafo ela fica (é o `pre-wrap` que
 * a desenha); na lista e na citação ela some, porque o bloco já quebra a linha
 * — e uma quebra a mais abriria uma linha em branco embaixo de toda lista.
 *
 * `noComecoDaLinha` é falso quando o pedaço vem logo depois de um bloco de
 * código na mesma linha: ali um `- ` não está no começo de linha nenhuma.
 */
function blocosDoTexto(
  texto: string,
  noComecoDaLinha: boolean,
  opacos: Opacos,
  saida: Bloco[],
): void {
  const linhas = texto.split('\n');
  let paragrafo = '';
  const fecharParagrafo = (): void => {
    if (paragrafo) saida.push({ tipo: 'paragrafo', trechos: trechosDaLinha(paragrafo, opacos) });
    paragrafo = '';
  };

  linhas.forEach((linha, indice) => {
    const quebra = indice < linhas.length - 1 ? '\n' : '';
    const podeSerBloco = indice > 0 || noComecoDaLinha;
    const ultimo = saida[saida.length - 1];

    const lista = podeSerBloco ? LINHA_DE_LISTA.exec(linha) : null;
    const numerada = podeSerBloco && !lista ? LINHA_NUMERADA.exec(linha) : null;
    const citacao = podeSerBloco && !lista && !numerada ? LINHA_DE_CITACAO.exec(linha) : null;

    if (lista || numerada) {
      const ordenada = Boolean(numerada);
      const item: ItemDeLista = {
        numero: numerada ? Number(numerada[1]) : null,
        trechos: trechosDaLinha(numerada ? numerada[2]! : lista![1]!, opacos),
      };
      if (!paragrafo && ultimo?.tipo === 'lista' && ultimo.ordenada === ordenada) {
        saida[saida.length - 1] = { ...ultimo, itens: [...ultimo.itens, item] };
      } else {
        fecharParagrafo();
        saida.push({ tipo: 'lista', ordenada, itens: [item] });
      }
      return;
    }

    if (citacao) {
      const trechos = trechosDaLinha(citacao[1]!, opacos);
      if (!paragrafo && ultimo?.tipo === 'citacao') {
        saida[saida.length - 1] = {
          tipo: 'citacao',
          trechos: [...ultimo.trechos, { tipo: 'texto', texto: '\n' }, ...trechos],
        };
      } else {
        fecharParagrafo();
        saida.push({ tipo: 'citacao', trechos });
      }
      return;
    }

    // O parágrafo junta as linhas e as quebras; os estilos são achados linha a
    // linha, porque não atravessam a quebra.
    paragrafo += linha + quebra;
  });

  fecharParagrafo();
}

// ── Os trechos de uma linha ────────────────────────────────────────────────

/** O que os estilos não cortam: código, menção e endereço. */
interface Opacos {
  /** As menções que a mensagem de fato fez, já como regex (ou `null`). */
  readonly mencoes: RegExp | null;
}

interface Atomo {
  readonly fim: number;
  readonly trecho: Trecho;
}

/**
 * Os trechos de um texto sem bloco de código. Pode ter quebras de linha (é o
 * parágrafo inteiro): cada linha é analisada sozinha, e as quebras voltam
 * entre elas como texto.
 */
function trechosDaLinha(texto: string, opacos: Opacos): Trecho[] {
  const saida: Trecho[] = [];
  texto.split('\n').forEach((linha, indice) => {
    if (indice > 0) juntarTexto(saida, '\n');
    for (const trecho of estilosDaLinha(linha, atomosDaLinha(linha, opacos))) {
      if (trecho.tipo === 'texto') juntarTexto(saida, trecho.texto);
      else saida.push(trecho);
    }
  });
  return saida;
}

function juntarTexto(saida: Trecho[], texto: string): void {
  const ultimo = saida[saida.length - 1];
  if (ultimo?.tipo === 'texto')
    saida[saida.length - 1] = { tipo: 'texto', texto: ultimo.texto + texto };
  else saida.push({ tipo: 'texto', texto });
}

/**
 * Código, menção e endereço, nesta ordem de precedência: dentro de código nada
 * é interpretado, e menção e endereço só são procurados no que sobrou.
 */
function atomosDaLinha(linha: string, opacos: Opacos): Map<number, Atomo> {
  const atomos = new Map<number, Atomo>();
  const livres: [number, number][] = [];

  // Código: o mesmo par de regras dos estilos, com precedência sobre eles.
  let cursor = 0;
  let i = linha.indexOf('`');
  while (i !== -1) {
    const fim = fechamentoDe(linha, i, linha.length, '`', () => false);
    if (fim === -1) {
      // Como nas cercas: sem fechamento para esta, não há para as seguintes.
      if (podeAbrir(linha, i) && i + 1 < linha.length && !ESPACO.test(linha[i + 1]!)) break;
      i = linha.indexOf('`', i + 1);
      continue;
    }
    if (i > cursor) livres.push([cursor, i]);
    atomos.set(i, { fim: fim + 1, trecho: { tipo: 'codigo', texto: linha.slice(i + 1, fim) } });
    cursor = fim + 1;
    i = linha.indexOf('`', cursor);
  }
  if (cursor < linha.length) livres.push([cursor, linha.length]);

  for (const [inicio, fim] of livres) {
    let resto: [number, number][] = [[inicio, fim]];

    if (opacos.mencoes) {
      resto = [];
      let antes = inicio;
      for (const achada of linha.slice(inicio, fim).matchAll(opacos.mencoes)) {
        const posicao = inicio + achada.index;
        if (posicao > antes) resto.push([antes, posicao]);
        atomos.set(posicao, {
          fim: posicao + achada[0].length,
          trecho: { tipo: 'mencao', texto: achada[0] },
        });
        antes = posicao + achada[0].length;
      }
      if (antes < fim) resto.push([antes, fim]);
    }

    for (const [de, ate] of resto) {
      let posicao = de;
      for (const pedaco of partir(linha.slice(de, ate))) {
        if (typeof pedaco === 'string') {
          posicao += pedaco.length;
          continue;
        }
        atomos.set(posicao, {
          fim: posicao + pedaco.url.length,
          trecho: { tipo: 'link', url: pedaco.url, href: pedaco.href },
        });
        posicao += pedaco.url.length;
      }
    }
  }

  return atomos;
}

/**
 * Onde fecha o marcador aberto em `i`, ou -1.
 *
 * O primeiro par que cumpre as regras: sem espaço encostado por dentro, e com
 * fronteira por fora. `opaco` diz as posições dentro de átomos, onde um
 * marcador não fecha — o `_` de uma URL não fecha itálico.
 */
function fechamentoDe(
  linha: string,
  i: number,
  ate: number,
  marcador: string,
  opaco: (j: number) => boolean,
): number {
  if (!podeAbrir(linha, i) || i + 1 >= ate || ESPACO.test(linha[i + 1]!)) return -1;
  for (let j = i + 2; j < ate; j++) {
    if (opaco(j)) continue;
    if (linha[j] === marcador && !ESPACO.test(linha[j - 1]!) && podeFechar(linha, j + 1)) return j;
  }
  return -1;
}

/** Os estilos de uma linha, com os átomos inteiros dentro deles. */
function estilosDaLinha(linha: string, atomos: Map<number, Atomo>): Trecho[] {
  const opaco = new Uint8Array(linha.length);
  for (const [inicio, atomo] of atomos) opaco.fill(1, inicio, atomo.fim);

  /**
   * `abertos`: o mesmo estilo não se aninha nele mesmo (negrito dentro de
   * negrito não muda nada). É também o que limita a profundidade a três — e
   * a análise a um tempo linear, por mais marcadores que o cliente mande.
   */
  const analisar = (de: number, ate: number, abertos: ReadonlySet<Estilo>): Trecho[] => {
    const saida: Trecho[] = [];
    // Um marcador que não achou fechamento neste trecho: os seguintes do
    // mesmo tipo também não acham, porque procurariam num pedaço menor.
    const semPar = new Set<string>();
    let texto = '';
    const descarregar = (): void => {
      if (texto) saida.push({ tipo: 'texto', texto });
      texto = '';
    };

    let i = de;
    while (i < ate) {
      const atomo = atomos.get(i);
      if (atomo) {
        descarregar();
        saida.push(atomo.trecho);
        i = atomo.fim;
        continue;
      }

      const caractere = linha[i]!;
      const estilo = ESTILO_DO_MARCADOR[caractere];
      if (estilo && !abertos.has(estilo) && !semPar.has(caractere)) {
        const fim = fechamentoDe(linha, i, ate, caractere, (j) => opaco[j] === 1);
        if (fim !== -1) {
          descarregar();
          saida.push({
            tipo: estilo,
            filhos: analisar(i + 1, fim, new Set([...abertos, estilo])),
          });
          i = fim + 1;
          continue;
        }
        if (podeAbrir(linha, i) && i + 1 < ate && !ESPACO.test(linha[i + 1]!)) {
          semPar.add(caractere);
        }
      }

      texto += caractere;
      i += 1;
    }

    descarregar();
    return saida;
  };

  return analisar(0, linha.length, new Set());
}

// ── A entrada ──────────────────────────────────────────────────────────────

/**
 * O texto de uma mensagem em blocos formatados.
 *
 * `mencoes` são os nomes que a nota de fato mencionou (#569), vindos da API:
 * um "@alguém" que ninguém escolheu na lista fica como texto comum.
 */
export function analisar(texto: string, mencoes: readonly string[] = []): Bloco[] {
  const opacos: Opacos = {
    mencoes:
      mencoes.length > 0
        ? new RegExp(
            // Nomes mais longos primeiro: "@Ana Paula" não pode virar "@Ana" + " Paula".
            [...mencoes]
              .sort((a, b) => b.length - a.length)
              .map((nome) => `@${nome}`.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
              .join('|'),
            'g',
          )
        : null,
  };

  const saida: Bloco[] = [];
  let cursor = 0;
  let noComecoDaLinha = true;

  for (const cerca of cercas(texto)) {
    if (cerca.inicio > cursor)
      blocosDoTexto(texto.slice(cursor, cerca.inicio), noComecoDaLinha, opacos, saida);
    saida.push({ tipo: 'bloco-de-codigo', texto: cerca.conteudo });
    cursor = cerca.fim;
    // A quebra logo depois do bloco é a dele, como a de uma linha de lista.
    if (texto[cursor] === '\n') {
      cursor += 1;
      noComecoDaLinha = true;
    } else {
      noComecoDaLinha = false;
    }
  }
  if (cursor < texto.length) blocosDoTexto(texto.slice(cursor), noComecoDaLinha, opacos, saida);

  return saida;
}

function trechosEmTexto(trechos: readonly Trecho[]): string {
  return trechos
    .map((trecho) => {
      switch (trecho.tipo) {
        case 'texto':
        case 'mencao':
        case 'codigo':
          return trecho.texto;
        case 'link':
          return trecho.url;
        default:
          return trechosEmTexto(trecho.filhos);
      }
    })
    .join('');
}

/**
 * O texto sem os marcadores, para onde não cabe formatação: a prévia da
 * última mensagem na lista de conversas.
 *
 * A lista numerada mantém o número — é conteúdo, "o item 2". O marcador de
 * item e o `> ` da citação saem.
 */
export function textoSemMarcadores(texto: string): string {
  const blocos = analisar(texto);
  return blocos
    .map((bloco, indice) => {
      const depois = indice < blocos.length - 1 && bloco.tipo !== 'paragrafo' ? '\n' : '';
      switch (bloco.tipo) {
        case 'paragrafo':
          return trechosEmTexto(bloco.trechos);
        case 'lista':
          return (
            bloco.itens
              .map(
                (item) =>
                  `${item.numero === null ? '' : `${item.numero}. `}${trechosEmTexto(item.trechos)}`,
              )
              .join('\n') + depois
          );
        case 'citacao':
          return trechosEmTexto(bloco.trechos) + depois;
        case 'bloco-de-codigo':
          return bloco.texto + depois;
      }
    })
    .join('');
}
